import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, COMPANY } from "@/lib/helpers";

function fmtUSD(n) {
  if (typeof n === "string") return n;
  return `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtTonnes(n) {
  return `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} t`;
}

export default function DeliveryNotePrint() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/delivery-notes/${id}`); setD(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, [id]);

  useEffect(() => {
    if (d) setTimeout(() => window.print(), 500);
  }, [d]);

  if (!d) return <div className="p-10">Loading…</div>;

  return (
    <div className="print-area max-w-3xl mx-auto p-10 bg-white text-black">
      <div className="no-print mb-6 flex items-center justify-between">
        <div className="phizo-label">Print Preview</div>
        <button onClick={() => window.print()} className="phizo-btn-primary">Print / Save PDF</button>
      </div>

      {/* Header */}
      <div className="border-2 border-[#09090b]">
        <div className="flex items-center justify-between p-5 border-b-2 border-[#09090b] bg-[#09090b] text-white">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="Phizo" className="h-12 w-12 object-contain bg-white p-1" />
            <div>
              <div className="font-heading text-2xl font-black tracking-tighter">{COMPANY.name}</div>
              <div className="text-xs uppercase tracking-widest text-white/70">{COMPANY.address}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="phizo-label text-white/70">Delivery Note</div>
            <div className="font-heading text-3xl font-black tracking-tighter mono">{d.number}</div>
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 border-b border-[#09090b]">
          <div className="p-4 border-r border-[#09090b]">
            <div className="phizo-label mb-1">Delivery Date</div>
            <div className="font-heading text-xl font-bold">{fmtDate(d.delivery_date)}</div>
          </div>
          <div className="p-4">
            <div className="phizo-label mb-1">Issued</div>
            <div className="font-heading text-xl font-bold">{fmtDate(d.created_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 border-b border-[#09090b]">
          <div className="p-4 border-r border-[#09090b]">
            <div className="phizo-label mb-1">Delivered To</div>
            <div className="font-semibold text-lg">{d.customer}</div>
            {d.project && <div className="text-sm text-[#52525b] mt-1">Project: {d.project}</div>}
          </div>
          <div className="p-4">
            <div className="phizo-label mb-1">Issued By</div>
            <div className="font-semibold">{d.created_by_name}</div>
            <div className="text-sm text-[#52525b]">{COMPANY.name}</div>
          </div>
        </div>

        {/* Item */}
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#09090b]">
              <th className="text-left p-3 phizo-label">Material Description</th>
              <th className="text-right p-3 phizo-label">Tonnes</th>
              <th className="text-right p-3 phizo-label">Rate (USD)</th>
              <th className="text-right p-3 phizo-label">Value (USD)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#09090b]">
              <td className="p-4">
                <div className="font-heading text-xl font-bold">{d.product_name}</div>
                <div className="text-xs mono text-[#52525b]">Code: {d.product_code}</div>
              </td>
              <td className="p-4 text-right font-heading text-2xl font-black">{fmtTonnes(d.tonnes)}</td>
              <td className="p-4 text-right mono">{fmtUSD(d.rate_usd)}</td>
              <td className="p-4 text-right font-heading text-xl font-black">{fmtUSD(d.tonnes * d.rate_usd)}</td>
            </tr>
          </tbody>
        </table>

        {/* Vehicle */}
        <div className="grid grid-cols-2 border-t-2 border-[#09090b]">
          <div className="p-4 border-r border-[#09090b]">
            <div className="phizo-label mb-1">Truck Plate</div>
            <div className="font-heading text-xl font-bold mono">{d.truck_plate || "—"}</div>
          </div>
          <div className="p-4">
            <div className="phizo-label mb-1">Driver</div>
            <div className="font-heading text-xl font-bold">{d.driver_name || "—"}</div>
          </div>
        </div>

        {d.notes && (
          <div className="p-4 border-t border-[#e4e4e7]">
            <div className="phizo-label mb-1">Notes</div>
            <div className="text-sm whitespace-pre-line">{d.notes}</div>
          </div>
        )}

        {/* Signatures */}
        <div className="grid grid-cols-2 border-t-2 border-[#09090b]">
          <div className="p-6 border-r border-[#09090b]">
            <div className="phizo-label mb-1">Roads Team Sign-off</div>
            {d.roads_team_signoff ? (
              <>
                <div className="font-heading text-lg font-bold mt-2">{d.roads_team_signoff}</div>
                <div className="border-t border-[#09090b] mt-3 pt-1 text-xs text-[#52525b]">Verified</div>
              </>
            ) : (
              <div className="h-20 border-b border-[#09090b] flex items-end pb-1 text-xs text-[#a1a1aa]">
                Sign:
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="phizo-label mb-1">Contract Manager Sign-off</div>
            {d.contract_manager_signoff ? (
              <>
                <div className="font-heading text-lg font-bold mt-2">{d.contract_manager_signoff}</div>
                <div className="border-t border-[#09090b] mt-3 pt-1 text-xs text-[#52525b]">Verified</div>
              </>
            ) : (
              <div className="h-20 border-b border-[#09090b] flex items-end pb-1 text-xs text-[#a1a1aa]">
                Sign:
              </div>
            )}
          </div>
        </div>

        {d.signed_at && (
          <div className="p-3 bg-[#f4f4f5] text-center text-xs mono text-[#52525b] border-t border-[#e4e4e7]">
            Confirmed at {fmtDateTime(d.signed_at)}
          </div>
        )}
      </div>

      <div className="text-center text-xs text-[#a1a1aa] mt-6">
        This is an official delivery note from {COMPANY.name}. Both signatures required for invoice processing.
      </div>
    </div>
  );
}
